import { Box, Divider, Typography } from "@mui/material"
import type { FC } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { AP_AGING_BUCKET_TO_LABEL, AP_AGING_ELIGIBLE_STATUSES, type ApAgingStatus } from "../../constants"
import type { ApAgingReportSearchParams } from "../../types/searchParams"

import { DateRangeFilter } from "@/components/common/Filters/DateRangeFilter"
import { FilterCheckbox } from "@/components/common/Filters/FilterCheckbox"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { ApAgingBucket } from "@/graphql/codegen/graphql"
import { INVOICE_STATUS_LABELS } from "@/screens/Accounting/subfeatures/Invoices/constants/labels"

interface ApAgingFilterDrawerProps {
  open: boolean
  onClose: () => void
  searchParams: ApAgingReportSearchParams
  onChange: <K extends keyof ApAgingReportSearchParams>(key: K, value: ApAgingReportSearchParams[K]) => void
  onBatchChange: (updates: Partial<ApAgingReportSearchParams>) => void
  onClear: () => void
}

interface FilterFormValues {
  statuses: ApAgingStatus[]
  agingBuckets: ApAgingBucket[]
}

const BUCKET_ORDER = [
  ApAgingBucket.Current,
  ApAgingBucket.Days_1_30,
  ApAgingBucket.Days_31_60,
  ApAgingBucket.Days_61_90,
  ApAgingBucket.Over_90,
]

export const ApAgingFilterDrawer: FC<ApAgingFilterDrawerProps> = ({
  open,
  onClose,
  searchParams,
  onChange,
  onBatchChange,
  onClear,
}) => {
  const { statuses, agingBuckets = [], dueDateFrom, dueDateTo } = searchParams

  const methods = useForm<FilterFormValues>({ values: { statuses, agingBuckets } })

  const handleStatusesChange = (values: string[]) =>
    onChange(
      "statuses",
      AP_AGING_ELIGIBLE_STATUSES.filter((status) => values.includes(status))
    )

  const handleAgingBucketsChange = (values: string[]) => {
    const selectedBuckets = BUCKET_ORDER.filter((bucket) => values.includes(bucket))

    onChange("agingBuckets", selectedBuckets.length ? selectedBuckets : undefined)
  }

  const handleRangeChange = useCallback(
    (range: { from: string | undefined; to: string | undefined }) =>
      onBatchChange({ dueDateFrom: range.from, dueDateTo: range.to }),
    [onBatchChange]
  )

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onClose} clear={onClear} testId='ap-aging-report.filter-drawer'>
      <FormProvider {...methods}>
        <Box className='h-full min-w-80'>
          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Aging Bucket
            </Typography>
            <Divider />
            <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
              {BUCKET_ORDER.map((bucket) => (
                <FilterCheckbox
                  key={bucket}
                  control={methods.control}
                  name='agingBuckets'
                  label={AP_AGING_BUCKET_TO_LABEL[bucket]}
                  value={bucket}
                  values={agingBuckets}
                  onFilterChange={handleAgingBucketsChange}
                  data-testid={`ap-aging-report.filter-drawer.aging-bucket-checkbox-${bucket}`}
                />
              ))}
            </Box>
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Status
            </Typography>
            <Divider />
            <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
              {AP_AGING_ELIGIBLE_STATUSES.map((status) => (
                <FilterCheckbox
                  key={status}
                  control={methods.control}
                  name='statuses'
                  label={INVOICE_STATUS_LABELS[status]}
                  value={status}
                  values={statuses}
                  onFilterChange={handleStatusesChange}
                  data-testid={`ap-aging-report.filter-drawer.status-checkbox-${status}`}
                />
              ))}
            </Box>
          </Box>

          <Box className='flex flex-1 flex-col'>
            <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
              Due Date Range
            </Typography>
            <Divider />
            <DateRangeFilter
              from={dueDateFrom}
              to={dueDateTo}
              onRangeChange={handleRangeChange}
              fromTestId='ap-aging-report.filter-drawer.due-date-from-field'
              toTestId='ap-aging-report.filter-drawer.due-date-to-field'
            />
          </Box>
        </Box>
      </FormProvider>
    </SearchFilterDrawer>
  )
}
